import mixpanel from 'mixpanel-browser';

const TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined;
const enabled = !!TOKEN;

if (enabled) {
  mixpanel.init(TOKEN!, {
    debug: import.meta.env.DEV,
    persistence: 'localStorage',
    track_pageview: false,
    ignore_dnt: false,
    ip: false,
    api_host: 'https://api-eu.mixpanel.com',
    property_blacklist: ['$city', '$region', '$country_code', '$initial_referrer', '$referrer'],
  });
  mixpanel.register({ source: 'owner_web' });
}

export const analytics = {
  // Must be called after login / session restore — sets venue context on every subsequent event
  identify(venueId: string, venueName: string) {
    if (!enabled) return;
    mixpanel.identify(venueId);
    mixpanel.register({ venue_id: venueId, venue_name: venueName });
    mixpanel.people.set({ $name: venueName, venue_id: venueId });
  },

  reset() {
    if (!enabled) return;
    mixpanel.reset();
  },

  page(path: string) {
    if (!enabled) return;
    mixpanel.track('Page Viewed', { path });
  },

  // ── Auth ────────────────────────────────────────────────────────────────────

  venueLoggedIn(venueId: string, venueName: string) {
    if (!enabled) return;
    mixpanel.track('Venue Logged In', { venue_id: venueId, venue_name: venueName });
  },

  venueRegistered(venueId: string, venueName: string) {
    if (!enabled) return;
    mixpanel.track('Venue Registered', { venue_id: venueId, venue_name: venueName });
  },

  venueLoggedOut() {
    if (!enabled) return;
    mixpanel.track('Venue Logged Out');
  },

  // ── Programs ────────────────────────────────────────────────────────────────

  programCreated(props: { programId: string; programName: string; stampsRequired: number; rewardDescription: string; rewardExpiryDays: number }) {
    if (!enabled) return;
    mixpanel.track('Program Created', props);
  },

  programEdited(props: { programId: string; stampsRequired: number; rewardDescription: string; rewardExpiryDays: number }) {
    if (!enabled) return;
    mixpanel.track('Program Edited', props);
  },

  programPaused(props: { programId: string; programName: string }) {
    if (!enabled) return;
    mixpanel.track('Program Paused', props);
  },

  programActivated(props: { programId: string; programName: string }) {
    if (!enabled) return;
    mixpanel.track('Program Activated', props);
  },

  // ── Stamps ──────────────────────────────────────────────────────────────────

  stampIssued(props: { method: 'order_qr' | 'phone' | 'patron_detail'; patronPhone?: string; patronId?: string; billTotalPaise?: number; currentStamps: number; stampsRequired: number; rewardGenerated: boolean }) {
    if (!enabled) return;
    mixpanel.track('Stamp Issued', props);
  },

  orderCreated(props: { orderType: 'itemised' | 'quick'; totalPaise: number; itemCount?: number; hasPatronPhone: boolean }) {
    if (!enabled) return;
    mixpanel.track('Order Created', props);
  },

  // ── Rewards ─────────────────────────────────────────────────────────────────

  rewardVerified(props: { rewardCode: string; patronName: string | null; patronPhone: string | null; patronEmail?: string | null; programName: string; status: string }) {
    if (!enabled) return;
    mixpanel.track('Reward Verified', props);
  },

  rewardRedeemed(props: { rewardCode: string; patronName: string | null; patronPhone: string | null; patronEmail?: string | null; programName: string; rewardDescription: string }) {
    if (!enabled) return;
    mixpanel.track('Reward Redeemed', props);
  },

  // ── Patrons ──────────────────────────────────────────────────────────────────

  patronExported(props: { count: number }) {
    if (!enabled) return;
    mixpanel.track('Patron List Exported', props);
  },

  // ── Settings ─────────────────────────────────────────────────────────────────

  settingsSaved() {
    if (!enabled) return;
    mixpanel.track('Settings Saved');
  },

  demoDatabaseSeeded() {
    if (!enabled) return;
    mixpanel.track('Demo Database Seeded');
  },

  // ── Landing ──────────────────────────────────────────────────────────────────

  ctaClicked(props: { label: string; section: string }) {
    if (!enabled) return;
    mixpanel.track('CTA Clicked', props);
  },
};
